// Routing decision of a task (PRD §33.1, GET /v1/tasks/{id}/routing-decisions, newest first): classifier and
// confidence, class, agent, tier, model, provider, cache and session affinity, the reason, premium review and
// escalation, the rejected candidates, and the previous decisions of the task.
import type { ReactNode } from "react";
import { useI18n } from "../../i18n/index.tsx";
import { listTaskRoutingDecisions } from "../../api/endpoints-routing.ts";
import type { RoutingDecision } from "../../api/types-routing.ts";
import { useAsync, type AsyncState } from "../../lib/useAsync.ts";
import { ErrorBanner, LoadingBlock } from "../../components/Feedback.tsx";
import { Pill } from "../../components/Status.tsx";
import { formatRatio } from "../agents/shared.tsx";
import { labelOr } from "../wave5/common.tsx";
import { TierPill, cacheLabel, classLabel, classifierLabel, sourceLabel } from "./shared.tsx";

type Decisions = { items: RoutingDecision[] } | null;

/** One request shared by the summary row and the section of the task detail. */
export function useTaskRouting(taskId: string | null, version: number): AsyncState<Decisions> {
  return useAsync<Decisions>((signal) => (taskId ? listTaskRoutingDecisions(taskId, signal) : Promise.resolve(null)), [taskId, version]);
}

/** "Agent / model" of the latest decision, or "not reported yet". */
export function RoutingSummary({ state }: { state: AsyncState<Decisions> }) {
  const { t } = useI18n();
  const d = state.data?.items[0];
  if (!d) return <span className="subtle">{t("task.notReportedYet")}</span>;
  return (
    <span data-testid="task-agent-model">
      <span className="user-content">{d.agent_name ?? "—"}</span> · <span className="mono">{d.model_display ?? d.external_model_id ?? "—"}</span>
    </span>
  );
}

export function RoutingDecisionSection({ taskId, state }: { taskId: string; state: AsyncState<Decisions> }) {
  const { t, locale, formatDateTime } = useI18n();
  const items = state.data?.items ?? [];
  const d = items[0];
  const headingId = `routing-${taskId}`;
  return (
    <section data-testid="routing-decision" aria-labelledby={headingId}>
      <h3 id={headingId} className="detail-heading">
        {t("ri.decision.title")}
      </h3>
      {state.loading ? (
        <LoadingBlock label={t("common.loading")} lines={2} />
      ) : state.error && !state.data ? (
        <ErrorBanner error={state.error} onRetry={state.reload} title={t("ri.decision.loadFailed")} />
      ) : !d ? (
        <p className="subtle">{t("ri.decision.none")}</p>
      ) : (
        <div className="stack">
          <div className="row row--wrap">
            <TierPill tier={d.applied_tier} />
            {d.premium_review_required ? (
              <Pill tone="warning" icon="alert">
                <span data-testid="routing-premium-review">{t("ri.decision.premiumReview")}</span>
              </Pill>
            ) : null}
            <span className="subtle">{t("ri.decision.decidedAt", { when: formatDateTime(d.created_at) })}</span>
          </div>
          {d.escalation_reason ? (
            <p className="ri-escalation" data-testid="routing-escalation">
              {t("ri.decision.escalated", { reason: labelOr(t, `ri.escalation.${d.escalation_reason}`, d.escalation_reason) })}
            </p>
          ) : null}
          <dl className="detail-grid">
            <Field label={t("ri.decision.classifier")} testId="routing-classifier">
              {classifierLabel(t, d)}
            </Field>
            <Field label={t("ri.decision.confidence")} testId="routing-confidence">
              {formatRatio(d.jev_confidence, locale)}
            </Field>
            <Field label={t("ri.decision.class")} testId="routing-class">
              {classLabel(t, d.task_class)}
            </Field>
            <Field label={t("ri.decision.agent")} testId="routing-agent">
              <span className="user-content">{d.agent_name ?? "—"}</span>
              {d.agent_profile_version_id ? <span className="subtle mono"> · {t("ri.decision.agentVersion", { id: d.agent_profile_version_id.slice(0, 8) })}</span> : null}
            </Field>
            <Field label={t("ri.decision.tier")} testId="routing-tier">
              <TierPill tier={d.applied_tier} />
              {d.requested_tier !== d.applied_tier ? <span className="subtle"> {t("ri.decision.requestedTier", { tier: labelOr(t, `ri.tier.${d.requested_tier}`, d.requested_tier) })}</span> : null}
            </Field>
            <Field label={t("ri.decision.model")} testId="routing-model">
              <span className="mono">{d.model_display ?? d.external_model_id ?? "—"}</span>
            </Field>
            <Field label={t("ri.decision.provider")} testId="routing-provider">
              {d.affinity?.effective_provider ?? "—"}
            </Field>
            <Field label={t("ri.decision.cache")} testId="routing-cache">
              {cacheLabel(t, d.affinity?.state ?? d.cache_state_at_selection)} ·{" "}
              {d.affinity?.cache_hit_ratio != null ? t("ri.decision.hit", { pct: formatRatio(d.affinity.cache_hit_ratio, locale) }) : t("ri.decision.hitUnknown")}
            </Field>
            <Field label={t("ri.decision.affinity")} testId="routing-affinity">
              {labelOr(t, `ri.affinity.${d.session_affinity}`, d.session_affinity)}
            </Field>
          </dl>
          <div>
            <p className="ri-label">{t("ri.decision.reason")}</p>
            <p className="user-content" data-testid="routing-reason">
              {d.decision_reason}
            </p>
          </div>
          {d.rejected_candidates.length === 0 ? (
            <p className="subtle">{t("ri.decision.noRejected")}</p>
          ) : (
            <details className="disclosure" data-testid="routing-rejected">
              <summary>{t("ri.decision.rejected", { n: d.rejected_candidates.length })}</summary>
              <ul className="council-reasons">
                {d.rejected_candidates.map((c, i) => (
                  <li key={`${c.model_target_id ?? "none"}-${i}`} data-testid="routing-rejected-candidate">
                    <span className="mono">{c.label ?? "—"}</span>
                    {c.tier ? <span className="subtle"> ({labelOr(t, `ri.tier.${c.tier}`, c.tier)})</span> : null}: <span className="user-content">{c.reason}</span>
                  </li>
                ))}
              </ul>
            </details>
          )}
          {items.length > 1 ? (
            <div>
              <p className="ri-label">{t("ri.decision.history")}</p>
              <ul className="ri-history" data-testid="routing-history">
                {items.slice(1).map((h) => (
                  <li key={h.id} data-testid="routing-history-item">
                    <span className="subtle">{formatDateTime(h.created_at)}</span>
                    <TierPill tier={h.applied_tier} />
                    <span className="mono">{h.model_display ?? h.external_model_id ?? "—"}</span>
                    <span className="subtle">{sourceLabel(t, h.source)}</span>
                  </li>
                ))}
              </ul>
            </div>
          ) : null}
        </div>
      )}
    </section>
  );
}

function Field({ label, testId, children }: { label: string; testId: string; children: ReactNode }) {
  return (
    <div>
      <dt>{label}</dt>
      <dd data-testid={testId}>{children}</dd>
    </div>
  );
}
